import type { Character, Organization, Relationship } from '@/lib/types';

export type OrganizationTitle = {
  id: string;
  name: string;
};

export type OrganizationMember = {
  characterId: string;
  title?: string;
  titleId?: string;
};

type UpdateEntityItem = (
  type: 'characters' | 'organizations',
  entityId: string,
  updates: Record<string, unknown>,
) => Promise<void>;

type RelationshipPersistence = {
  saveRelationship?: (relationship: Relationship) => Promise<void>;
  deleteRelationship?: (relationshipId: string) => Promise<void>;
};

type OrganizationMembershipLike = {
  organizationMembers?: OrganizationMember[] | null;
};

type OrganizationHierarchyLike = {
  parentOrganizationIds?: string[] | null;
  childOrganizationIds?: string[] | null;
  childOrganizationTitles?: Record<string, string> | null;
};

const unique = (values: string[]) => Array.from(new Set(values.filter(Boolean)));

function toSafeRelationshipIdSegment(value: string) {
  return value.replace(/[^A-Za-z0-9_-]/g, '_');
}

export function getOrganizationMembershipRelationshipId(characterId: string, organizationId: string) {
  return [
    'organization_membership',
    toSafeRelationshipIdSegment(characterId),
    toSafeRelationshipIdSegment(organizationId),
  ].join('__');
}

function createOrganizationMembershipRelationship(characterId: string, organizationId: string): Relationship {
  return {
    id: getOrganizationMembershipRelationshipId(characterId, organizationId),
    entityAId: characterId,
    entityAType: 'characters',
    entityBId: organizationId,
    entityBType: 'organizations',
    forwardDescription: 'is a member of',
    managedBy: {
      type: 'organization_membership',
      characterId,
      organizationId,
    },
  };
}

export function getOrganizationPartOfRelationshipId(childOrganizationId: string, parentOrganizationId: string) {
  return [
    'organization_part_of',
    toSafeRelationshipIdSegment(childOrganizationId),
    toSafeRelationshipIdSegment(parentOrganizationId),
  ].join('__');
}

function createOrganizationPartOfRelationship(childOrganizationId: string, parentOrganizationId: string): Relationship {
  return {
    id: getOrganizationPartOfRelationshipId(childOrganizationId, parentOrganizationId),
    entityAId: childOrganizationId,
    entityAType: 'organizations',
    entityBId: parentOrganizationId,
    entityBType: 'organizations',
    forwardDescription: 'is part of',
    managedBy: {
      type: 'organization_part_of',
      childOrganizationId,
      parentOrganizationId,
    },
  };
}

async function syncOrganizationMembershipRelationship({
  characterId,
  organizationId,
  shouldExist,
  saveRelationship,
  deleteRelationship,
}: {
  characterId: string;
  organizationId: string;
  shouldExist: boolean;
} & RelationshipPersistence) {
  const relationshipId = getOrganizationMembershipRelationshipId(characterId, organizationId);

  if (shouldExist) {
    await saveRelationship?.(createOrganizationMembershipRelationship(characterId, organizationId));
    return;
  }

  await deleteRelationship?.(relationshipId);
}

async function syncOrganizationPartOfRelationship({
  childOrganizationId,
  parentOrganizationId,
  shouldExist,
  saveRelationship,
  deleteRelationship,
}: {
  childOrganizationId: string;
  parentOrganizationId: string;
  shouldExist: boolean;
} & RelationshipPersistence) {
  const relationshipId = getOrganizationPartOfRelationshipId(childOrganizationId, parentOrganizationId);

  if (shouldExist) {
    await saveRelationship?.(createOrganizationPartOfRelationship(childOrganizationId, parentOrganizationId));
    return;
  }

  await deleteRelationship?.(relationshipId);
}

export const getOrganizationMemberIds = (organization: OrganizationMembershipLike | null | undefined) => (
  unique((organization?.organizationMembers || []).map((member) => member.characterId))
);

export const getParentOrganizationIds = (organization: OrganizationHierarchyLike | null | undefined) => (
  unique(organization?.parentOrganizationIds || [])
);

export const getChildOrganizationIds = (organization: OrganizationHierarchyLike | null | undefined) => (
  unique(organization?.childOrganizationIds || [])
);

export async function syncOrganizationsForCharacter({
  characterId,
  previousOrganizationIds,
  nextOrganizationIds,
  organizations,
  updateEntityItem,
  saveRelationship,
  deleteRelationship,
}: {
  characterId: string;
  previousOrganizationIds: string[];
  nextOrganizationIds: string[];
  organizations: Organization[];
  updateEntityItem: UpdateEntityItem;
} & RelationshipPersistence) {
  const previous = new Set(previousOrganizationIds);
  const next = new Set(nextOrganizationIds);
  const affectedOrganizationIds = unique([...previousOrganizationIds, ...nextOrganizationIds]);

  await Promise.all(affectedOrganizationIds.map(async (organizationId) => {
    const organization = organizations.find((item) => item.id === organizationId);
    if (!organization) return;

    const shouldHaveCharacter = next.has(organizationId);
    const hadCharacter = previous.has(organizationId);
    const existingMembers = organization.organizationMembers || [];
    const hasCharacter = existingMembers.some((member) => member.characterId === characterId);

    if (shouldHaveCharacter && !hasCharacter) {
      await updateEntityItem('organizations', organizationId, {
        organizationMembers: [...existingMembers, { characterId }],
      });
    }

    if (!shouldHaveCharacter && hasCharacter) {
      await updateEntityItem('organizations', organizationId, {
        organizationMembers: existingMembers.filter((member) => member.characterId !== characterId),
      });
    }

    if (shouldHaveCharacter !== hadCharacter) {
      await syncOrganizationMembershipRelationship({
        characterId,
        organizationId,
        shouldExist: shouldHaveCharacter,
        saveRelationship,
        deleteRelationship,
      });
      return;
    }
  }));
}

export async function syncCharactersForOrganization({
  organizationId,
  previousCharacterIds,
  nextCharacterIds,
  characters,
  updateEntityItem,
  saveRelationship,
  deleteRelationship,
}: {
  organizationId: string;
  previousCharacterIds: string[];
  nextCharacterIds: string[];
  characters: Character[];
  updateEntityItem: UpdateEntityItem;
} & RelationshipPersistence) {
  const previous = new Set(previousCharacterIds);
  const next = new Set(nextCharacterIds);
  const affectedCharacterIds = unique([...previousCharacterIds, ...nextCharacterIds]);

  await Promise.all(affectedCharacterIds.map(async (characterId) => {
    const character = characters.find((item) => item.id === characterId);
    if (!character) return;

    const shouldHaveOrganization = next.has(characterId);
    const hadOrganization = previous.has(characterId);
    const currentOrganizationIds = unique(character.organizationIds || []);
    const hasOrganization = currentOrganizationIds.includes(organizationId);

    if (shouldHaveOrganization && !hasOrganization) {
      await updateEntityItem('characters', characterId, {
        organizationIds: [...currentOrganizationIds, organizationId],
      });
    }

    if (!shouldHaveOrganization && hasOrganization) {
      await updateEntityItem('characters', characterId, {
        organizationIds: currentOrganizationIds.filter((id) => id !== organizationId),
      });
    }

    if (shouldHaveOrganization !== hadOrganization) {
      await syncOrganizationMembershipRelationship({
        characterId,
        organizationId,
        shouldExist: shouldHaveOrganization,
        saveRelationship,
        deleteRelationship,
      });
    }
  }));
}

export async function syncParentOrganizationsForOrganization({
  organizationId,
  previousParentOrganizationIds,
  nextParentOrganizationIds,
  organizations,
  updateEntityItem,
  saveRelationship,
  deleteRelationship,
}: {
  organizationId: string;
  previousParentOrganizationIds: string[];
  nextParentOrganizationIds: string[];
  organizations: Organization[];
  updateEntityItem: UpdateEntityItem;
} & RelationshipPersistence) {
  const previous = new Set(previousParentOrganizationIds);
  const next = new Set(nextParentOrganizationIds);
  const affectedParentOrganizationIds = unique([...previousParentOrganizationIds, ...nextParentOrganizationIds])
    .filter((parentOrganizationId) => parentOrganizationId !== organizationId);

  await Promise.all(affectedParentOrganizationIds.map(async (parentOrganizationId) => {
    const parentOrganization = organizations.find((item) => item.id === parentOrganizationId);
    if (!parentOrganization) return;

    const shouldHaveChild = next.has(parentOrganizationId);
    const hadParent = previous.has(parentOrganizationId);
    const currentChildOrganizationIds = unique(parentOrganization.childOrganizationIds || []);
    const hasChild = currentChildOrganizationIds.includes(organizationId);

    if (shouldHaveChild && !hasChild) {
      await updateEntityItem('organizations', parentOrganizationId, {
        childOrganizationIds: [...currentChildOrganizationIds, organizationId],
      });
    }

    if (!shouldHaveChild && hasChild) {
      const { [organizationId]: _removedTitle, ...childOrganizationTitles } = parentOrganization.childOrganizationTitles || {};
      await updateEntityItem('organizations', parentOrganizationId, {
        childOrganizationIds: currentChildOrganizationIds.filter((id) => id !== organizationId),
        childOrganizationTitles,
      });
    }

    if (shouldHaveChild !== hadParent) {
      await syncOrganizationPartOfRelationship({
        childOrganizationId: organizationId,
        parentOrganizationId,
        shouldExist: shouldHaveChild,
        saveRelationship,
        deleteRelationship,
      });
    }
  }));
}

export async function syncChildOrganizationsForOrganization({
  organizationId,
  previousChildOrganizationIds,
  nextChildOrganizationIds,
  organizations,
  updateEntityItem,
  saveRelationship,
  deleteRelationship,
}: {
  organizationId: string;
  previousChildOrganizationIds: string[];
  nextChildOrganizationIds: string[];
  organizations: Organization[];
  updateEntityItem: UpdateEntityItem;
} & RelationshipPersistence) {
  const previous = new Set(previousChildOrganizationIds);
  const next = new Set(nextChildOrganizationIds);
  const affectedChildOrganizationIds = unique([...previousChildOrganizationIds, ...nextChildOrganizationIds])
    .filter((childOrganizationId) => childOrganizationId !== organizationId);

  await Promise.all(affectedChildOrganizationIds.map(async (childOrganizationId) => {
    const childOrganization = organizations.find((item) => item.id === childOrganizationId);
    if (!childOrganization) return;

    const shouldHaveParent = next.has(childOrganizationId);
    const hadChild = previous.has(childOrganizationId);
    const currentParentOrganizationIds = unique(childOrganization.parentOrganizationIds || []);
    const hasParent = currentParentOrganizationIds.includes(organizationId);

    if (shouldHaveParent && !hasParent) {
      await updateEntityItem('organizations', childOrganizationId, {
        parentOrganizationIds: [...currentParentOrganizationIds, organizationId],
      });
    }

    if (!shouldHaveParent && hasParent) {
      await updateEntityItem('organizations', childOrganizationId, {
        parentOrganizationIds: currentParentOrganizationIds.filter((id) => id !== organizationId),
      });
    }

    if (shouldHaveParent !== hadChild) {
      await syncOrganizationPartOfRelationship({
        childOrganizationId,
        parentOrganizationId: organizationId,
        shouldExist: shouldHaveParent,
        saveRelationship,
        deleteRelationship,
      });
    }
  }));
}

export async function removeOrganizationFromCharacters({
  organizationId,
  characters,
  updateEntityItem,
  organizationMembers = [],
  deleteRelationship,
}: {
  organizationId: string;
  characters: Character[];
  updateEntityItem: UpdateEntityItem;
  organizationMembers?: OrganizationMember[];
} & Pick<RelationshipPersistence, 'deleteRelationship'>) {
  const affectedCharacters = characters.filter((character) => character.organizationIds?.includes(organizationId));
  const relationshipCharacterIds = unique([
    ...affectedCharacters.map((character) => character.id),
    ...organizationMembers.map((member) => member.characterId),
  ]);

  await Promise.all([
    ...affectedCharacters.map((character) => (
      updateEntityItem('characters', character.id, {
        organizationIds: (character.organizationIds || []).filter((id) => id !== organizationId),
      })
    )),
    ...relationshipCharacterIds.map((characterId) => (
      deleteRelationship?.(getOrganizationMembershipRelationshipId(characterId, organizationId))
    )),
  ]);
}

export async function removeOrganizationFromOrganizations({
  organizationId,
  organizations,
  updateEntityItem,
  parentOrganizationIds = [],
  childOrganizationIds = [],
  deleteRelationship,
}: {
  organizationId: string;
  organizations: Organization[];
  updateEntityItem: UpdateEntityItem;
  parentOrganizationIds?: string[];
  childOrganizationIds?: string[];
} & Pick<RelationshipPersistence, 'deleteRelationship'>) {
  const affectedParentOrganizations = organizations.filter((organization) => (
    organization.childOrganizationIds?.includes(organizationId)
  ));
  const affectedChildOrganizations = organizations.filter((organization) => (
    organization.parentOrganizationIds?.includes(organizationId)
  ));
  const relationshipParentOrganizationIds = unique([
    ...affectedParentOrganizations.map((organization) => organization.id),
    ...parentOrganizationIds,
  ]);
  const relationshipChildOrganizationIds = unique([
    ...affectedChildOrganizations.map((organization) => organization.id),
    ...childOrganizationIds,
  ]);

  await Promise.all([
    ...affectedParentOrganizations.map((organization) => {
      const { [organizationId]: _removedTitle, ...childOrganizationTitles } = organization.childOrganizationTitles || {};

      return updateEntityItem('organizations', organization.id, {
        childOrganizationIds: (organization.childOrganizationIds || []).filter((id) => id !== organizationId),
        childOrganizationTitles,
      });
    }),
    ...affectedChildOrganizations.map((organization) => (
      updateEntityItem('organizations', organization.id, {
        parentOrganizationIds: (organization.parentOrganizationIds || []).filter((id) => id !== organizationId),
      })
    )),
    ...relationshipParentOrganizationIds.map((parentOrganizationId) => (
      deleteRelationship?.(getOrganizationPartOfRelationshipId(organizationId, parentOrganizationId))
    )),
    ...relationshipChildOrganizationIds.map((childOrganizationId) => (
      deleteRelationship?.(getOrganizationPartOfRelationshipId(childOrganizationId, organizationId))
    )),
  ]);
}

export async function removeCharacterFromOrganizations({
  characterId,
  organizations,
  updateEntityItem,
  characterOrganizationIds = [],
  deleteRelationship,
}: {
  characterId: string;
  organizations: Organization[];
  updateEntityItem: UpdateEntityItem;
  characterOrganizationIds?: string[];
} & Pick<RelationshipPersistence, 'deleteRelationship'>) {
  const affectedOrganizations = organizations.filter((organization) => (
    organization.organizationMembers?.some((member) => member.characterId === characterId)
  ));
  const relationshipOrganizationIds = unique([
    ...affectedOrganizations.map((organization) => organization.id),
    ...characterOrganizationIds,
  ]);

  await Promise.all([
    ...affectedOrganizations.map((organization) => (
      updateEntityItem('organizations', organization.id, {
        organizationMembers: (organization.organizationMembers || []).filter((member) => member.characterId !== characterId),
      })
    )),
    ...relationshipOrganizationIds.map((organizationId) => (
      deleteRelationship?.(getOrganizationMembershipRelationshipId(characterId, organizationId))
    )),
  ]);
}
